import {createStorageManager} from '@sollapay/utils';

import {getInviteRedirectParams} from './location.utils';
import {getRememberedOrganization} from './remembered-organization.utils';

import type {LoginContext} from './login-params.utils';

export type AuthMode = 'member' | 'guest';

const MODE_KEY = 'auth:mode';
const GUEST_EMAIL_KEY = 'auth:guest-email';

const storage = () => createStorageManager({prefix: 'sollapay'});

export function getAuthMode(): AuthMode {
  return storage().getItem(MODE_KEY) === 'guest' ? 'guest' : 'member';
}

export function setAuthMode(mode: AuthMode): void {
  storage().setItem(MODE_KEY, mode);
}

export function clearAuthMode(): void {
  storage().removeItem(MODE_KEY);
  storage().removeItem(GUEST_EMAIL_KEY);
}

export function getGuestLoginHint(): string | undefined {
  return storage().getItem(GUEST_EMAIL_KEY) ?? undefined;
}

export function setGuestLoginHint(email: string): void {
  storage().setItem(GUEST_EMAIL_KEY, email);
}

// The provider picks its client once at mount, so guest mode takes effect on the full reload to `returnTo`.
export function enterGuestMode({email, returnTo}: {email?: string; returnTo: string}): void {
  clearAuthMode();
  setAuthMode('guest');
  if (email) setGuestLoginHint(email);
  window.location.replace(returnTo);
}

export function getLoginContext(mode: AuthMode): LoginContext {
  return mode === 'guest'
    ? {kind: 'guest', loginHint: getGuestLoginHint()}
    : {kind: 'member', organization: getRememberedOrganization()};
}

// A member invite link ends a sticky guest mode; the provider reads this once at mount, so a mode switch needs a full reload.
export function resolveLoginContextOnLoad(): LoginContext {
  if (getInviteRedirectParams()) clearAuthMode();
  return getLoginContext(getAuthMode());
}
